import React, { useEffect, useState } from 'react';
import '../styles/History.css';
import { checkOnAuthStateChanged, cloudFunctionV2, getDocument, getSubDocument } from '../functions';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header, Footer, Button, Loading, Pagination, SearchInput, PredictiveSearch, Modal } from '../components';
import moment from 'moment';
import { MagnifyingGlass } from '../assets/icons';
import { colour } from '../styles/GlobalStyles';

function History() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [loggedIn, setLoggedIn] = useState(null);
    const [searches, setSearches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [address, setAddress] = useState("");
    const [finalResults, setFinalResults] = useState([]);
    const [activeSearching, setActiveSearching] = useState(false);
    const [searchExpanded, setSearchExpanded] = useState(false);
    const [companyId, setCompanyId] = useState(null);
    const [showSearchModal, setShowSearchModal] = useState(false);
    const [searchProperty, setSearchProperty] = useState(null);
    const [credits, setCredits] = useState(0);

    // Ensure the "page" query‑param is a positive integer; default to 1
    const getValidPage = (val) => {
        const num = parseInt(val, 10);
        return Number.isNaN(num) || num < 1 ? 1 : num;
    };
    const [page, setPage] = useState(getValidPage(searchParams.get("page")));
    const navigate = useNavigate();

    useEffect(() => {
        document.title = "Search History | Clozers";

        const fetchCompanyInfo = async(newCompanyId) => {
            setCompanyId(newCompanyId);
            const url = process.env.REACT_APP_GET_ORDERED_DOCS;
            const params = {
                collection: "companies",
                subcollection: "properties",
                document: newCompanyId,
                field: "data.date",
                order: "desc",
                limit: 10,
                page: page
            };
            const queryData = await cloudFunctionV2(url, params);
            if ( queryData.status === 200 ) {
                const newSearches = [];
                queryData.data.forEach(doc => {
                    newSearches.push(doc.data);
                });
                setSearches(newSearches);
                setTotal(queryData.total);
                // If the current page exceeds the maximum possible page, reset to 1
                const totalPages = Math.ceil(queryData.total / params.limit);
                if (page > totalPages && totalPages > 0) {
                    setPage(1);
                    setSearchParams({ page: 1 });
                }
            }
        };

        const getCompanyData = async(newCompanyId) => {
            const colRef = "companies";
            const queryData = await getDocument(colRef, newCompanyId);
            if ( queryData.status === 200 ) {
                setCredits(queryData.data.data.credits);
            }
        };

        const fetchUserData = async() => {
            const user = await checkOnAuthStateChanged();
            if ( user.status === 200 ) {
                setLoggedIn(true);

                const colRef = "users";
                const queryData = await getDocument(colRef, user.userId);
                if ( queryData.status === 200 ) {
                    const userData = queryData.data.data;
                    if ( userData.companyId !== "" ) {
                        await fetchCompanyInfo(userData.companyId);
                        await getCompanyData(userData.companyId);
                    }
                }
            }
            else {
                setLoggedIn(false);
                navigate("/");
            }

            setLoading(false);
        };

        fetchUserData();

    }, [navigate, searchParams, page, setSearchParams]);
    
    const startSearch = () => {
        navigate("/");
    };

    const onPageChange = (index) => {
        setPage(index);
        setSearchParams({ page: index });
        setLoading(true);
    };

    const onChangeInput = async(val) => {
        setAddress(val);
        const url = process.env.REACT_APP_REAPI_AUTOCOMPLETE_URL;
        const autocompleteParams = {
            address: val
        };
        const getAutocomplete = await cloudFunctionV2(url, autocompleteParams);
        if ( getAutocomplete.status === 200 ) {
            const responses = getAutocomplete.data;
            if ( responses.length !== 0 ) {
                const updatedArray = responses.map((item) => {
                    return {
                        label: item.address,
                        id: item.id,
                        countyId: item.fips
                    }
                });
                setFinalResults(updatedArray);
                setActiveSearching(false);
            }
            else {
                setFinalResults([]);
                setActiveSearching(false);
            }
        }
        else {
            setFinalResults([]);
            setActiveSearching(false);
        }
    };

    const selectAutocompleteProperty = async(item) => {
        const colRef = "companies";
        const docRef = companyId;
        const subColRef = "properties";
        const subDocRef = item.id;
        const queryData = await getSubDocument(colRef, docRef, subColRef, subDocRef);
        if ( queryData.status === 200 ) {
            setAddress("");
            setFinalResults([]);
            window.open(`/run-comps/${item.countyId}/${item.id}`, "_blank");
        }
        else {
            setShowSearchModal(true);
            setSearchProperty(item);
            setFinalResults([]);
            setActiveSearching(false);
        }
    };

    const closeSearchModal = () => {
        setShowSearchModal(false);
        setSearchProperty(null);
    };

    const openSearch = async(item) => {
        if ( credits < 1 ) {
            navigate("/");
            alert("You have no credits left to use the platform - contact us for more details");
            return;
        }
       
        setLoading(true);
        setFinalResults([]);
        setActiveSearching(false);
        setAddress(item.label);
        const url = process.env.REACT_APP_REAPI_PROPERTY_DETAILS_URL;
        const params = {
            propertyId: item.id
        };
        const queryData = await cloudFunctionV2(url, params);
        if ( queryData.status === 200 ) {
            if ( queryData.data.propertyType !== "SFR" ) {
                navigate("/");
                alert("We only support Single Family Residences at the moment");
            }
            else {
                if (queryData.data.propertyInfo.bathrooms) {
                    const bathrooms = queryData.data.propertyInfo.bathrooms;
                    const decimalPart = Number((bathrooms % 1).toFixed(2));
                    let updatedBathrooms = bathrooms;
                    if (decimalPart === 0.67) {
                        updatedBathrooms = Math.floor(bathrooms) + 0.5;
                    }
                    queryData.data.propertyInfo.bathrooms = updatedBathrooms;
                }

                navigate("/verify-property", {
                    state: {
                        data: queryData.data,
                        companyId: companyId
                    }
                });
            }
        }
        setLoading(false);
    };

    return (
        <div className="history-outer-container">
            <Header
                loggedIn={loggedIn}
            />
            {
                showSearchModal === true &&
                <div className="dark-bg">
                </div>
            }
            <div className="history-inner-container relative-container">
                {
                    loading === true &&
                    <Loading />
                }
                {
                    showSearchModal === true &&
                    <Modal
                        heading="You haven't searched this property"
                        subheading={null}
                        body={<span className="body-regular colour-primary">You haven't searched for <span className="body-bold">{searchProperty.label}</span> before. Would you like to do so now?</span>}
                        type="secondary"
                        cancelText={"Cancel"}
                        cancelButtonCTA={closeSearchModal}
                        actionButtonText={"Yes, search now"}
                        actionButtonCTA={() => openSearch(searchProperty)}
                        secondaryButtonText={"No, don't search"}
                        secondaryButtonCTA={closeSearchModal}
                        exitButtonCTA={closeSearchModal}
                        loading={false}
                        actionButtonDisabled={false}
                        leftIcon={null}
                        rightIcon={null}
                        cancelLeftIcon={null}
                        cancelRightIcon={null}
                        customClass="history-search-modal"
                    />
                }
                <div className="history-search-container">
                    {
                        companyId === null ?
                        null
                        :
                        searchExpanded === true ?
                        <div className="history-search-expanded relative-container">
                            <SearchInput
                                value={address}
                                onChange={(text) => onChangeInput(text.target.value)}
                                placeholder="Search"
                                type="text"
                                clearInput={() => onChangeInput("")}
                                magnifyingGlassFunc={null}
                                disabled={false}
                            />
                            {
                                address === "" || (finalResults.length === 0 && activeSearching === false) ?
                                null
                                :
                                <PredictiveSearch
                                    loading={activeSearching}
                                    results={finalResults}
                                    selectItem={(item) => selectAutocompleteProperty(item)}
                                    nested={false}
                                />
                            }
                        </div>
                        :
                        <div 
                            className="button-row text-button gap-x-small"
                            onClick={() => setSearchExpanded(true)}
                        >
                            <MagnifyingGlass
                                fill={colour.gray06}
                                className=""
                                clickFunc={null}
                            />
                            <span className="body-regular colour-primary">
                                Search
                            </span>
                        </div>
                    }
                </div>
                <div className="history-title-container">
                    <h1 className="heading-large-bold colour-primary">
                        History
                    </h1>
                    <span className="body-regular colour-primary block-text text-align-center">
                        View recently searched properties
                    </span>
                </div>
                <div className="history-body-container">
                    {
                        loading === false && searches.length === 0 ?
                        <div className="history-body-search-container">
                            <Button
                                buttonType="primary-button"
                                size="large"
                                text={"Get started"}
                                cta={startSearch}
                                disabled={false}
                                leftIcon={null}
                                leftIconClass=""
                                rightIcon={null}
                                rightIconClass=""
                                alt=""
                                standardIcon={false}
                                badge={false}
                                badgeVal={null}
                                badgeBG={null}
                                buttonRef={null}
                                spinner={false}
                            />
                        </div>
                        :
                        searches.map((item, index) =>
                            <div 
                                className="history-search-element"
                                key={index}
                            >
                                <div className="history-search-element-left-container">
                                    <span className="body-bold colour-primary">
                                        {moment.unix(item.date._seconds).format("MMM DD, YYYY")}
                                    </span>
                                    <span className="body-regular colour-primary">
                                        {item.address.label}
                                    </span>
                                </div>
                                <div className="history-search-element-right-container">
                                    <a 
                                        href={`/run-comps/${item.countyId}/${item.propertyId}`}
                                        className="text-button cursor-pointer no-underline"
                                    >
                                        <span className="body-regular colour-primary">
                                            View
                                        </span>
                                    </a>
                                </div>
                            </div>
                        )
                    }
                </div>
                {
                    total > 10 &&
                    <Pagination
                        total={total}
                        limit={10}
                        currentPage={page}
                        onPageChange={onPageChange}
                        siblingCount={1}
                    />
                }
            </div>
            <Footer />
        </div>
    )
};

export default History;